import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useState } from 'react';
import { catalogByFrequency, type CassettoFrequency } from '@trackeat/shared';
import { useActiveBusiness } from '../../lib/auth/useActiveBusiness.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { toUserMessage } from '../../lib/errors.js';

interface EquipmentRow {
  id: string;
  catalog_id?: string | null;
  archived_at?: string | null;
}

interface AddVoceModalProps {
  isOpen: boolean;
  frequency: CassettoFrequency;
  onDismiss: () => void;
  onAdded?: () => void;
}

// Aggiunge una "voce" a un cassetto: una voce è una `cleaning_schedules` con la
// frequenza del cassetto. Da catalogo GSI → crea anche l'attrezzatura; da testo
// libero → solo la schedule (area/voce custom, equipment_id=null).
export function AddVoceModal({ isOpen, frequency, onDismiss, onAdded }: AddVoceModalProps): JSX.Element {
  const createMaster = useEnqueueMaster();
  const activeBusiness = useActiveBusiness();
  const { data: equipments = [] } = useMasters<EquipmentRow>('cleaning_equipments');
  const [freeText, setFreeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [presentToast] = useIonToast();
  const setToast = (m: string): void => {
    void presentToast({ message: m, duration: 1600, position: 'top' });
  };

  // Catalogo della frequenza, escludendo gli slug già presenti come attrezzatura.
  const usedSlugs = new Set(
    equipments.filter((e) => !e.archived_at && e.catalog_id).map((e) => e.catalog_id),
  );
  const chips = catalogByFrequency(frequency).filter((c) => !usedSlugs.has(c.slug));

  async function addCatalog(slug: string, label: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const ctx = await activeBusiness.resolve();
      if (!ctx) {
        setToast('Nessun locale attivo');
        return;
      }
      const eqId = crypto.randomUUID();
      await createMaster.mutateAsync({
        table: 'cleaning_equipments',
        id: eqId,
        op: 'insert',
        base_version: 0,
        patch: {
          id: eqId,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          name: label,
          kind: 'altro',
          catalog_id: slug,
        },
      });
      const schedId = crypto.randomUUID();
      await createMaster.mutateAsync({
        table: 'cleaning_schedules',
        id: schedId,
        op: 'insert',
        base_version: 0,
        patch: {
          id: schedId,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          title: `Sanificazione ${label}`,
          frequency,
          equipment_id: eqId,
          requires_photo: false,
        },
      });
      setToast(`Aggiunta: ${label}`);
      onAdded?.();
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function addFreeText(): Promise<void> {
    const title = freeText.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      const ctx = await activeBusiness.resolve();
      if (!ctx) {
        setToast('Nessun locale attivo');
        return;
      }
      const schedId = crypto.randomUUID();
      await createMaster.mutateAsync({
        table: 'cleaning_schedules',
        id: schedId,
        op: 'insert',
        base_version: 0,
        patch: {
          id: schedId,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          title,
          frequency,
          equipment_id: null,
          requires_photo: false,
        },
      });
      setToast(`Aggiunta: ${title}`);
      setFreeText('');
      onAdded?.();
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Aggiungi voce</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>Fine</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.4 }}>
            Tocca una voce standard del cassetto <b>{frequency}</b>, oppure scrivine
            una tua (attrezzatura o area da pulire).
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {chips.map((c) => (
              <button
                key={c.slug}
                type="button"
                disabled={busy}
                onClick={() => void addCatalog(c.slug, c.label)}
                style={{
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#0f172a',
                  borderRadius: 999,
                  padding: '7px 13px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                + {c.label}
              </button>
            ))}
            {chips.length === 0 && (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                Tutte le voci standard di questa frequenza sono già aggiunte.
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Voce personalizzata
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <IonInput
              value={freeText}
              placeholder="Es. Pavimento cucina, Bagni…"
              onIonInput={(e) => setFreeText(String(e.detail.value ?? ''))}
              style={{
                flex: 1,
                '--background': '#eef2f7',
                '--padding-start': '12px',
                '--padding-end': '12px',
                '--border-radius': '10px',
              } as React.CSSProperties}
            />
            <IonButton onClick={() => void addFreeText()} disabled={busy || !freeText.trim()}>
              Aggiungi
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}
